/**
 * Shared cart-state primitives: one deduplicated `/cart.js` read for the whole
 * page, plus the DOM-derived per-variant quantity helper the add-to-cart cap
 * uses.
 *
 * Classic script (this theme has no module graph) — it attaches to
 * `window.theme` and is loaded from layout/theme.liquid immediately after
 * theme.js, so every deferred consumer (attribution.js, the section-level
 * cart.js) sees it.
 *
 * Why a shared read: several components refresh cart state independently right
 * after an add (the product form's quantity sync, the upsell carousel's
 * data-attribute refresh, the drawer's freshness check). Fired in the same
 * tick they cannot reuse the HTTP cache, so they raced each other for
 * connections at the exact moment the drawer was animating open — on a store
 * where the IOM app embed already drives ~5-8 `/cart.js` reads a second with
 * the drawer open, and Shopify processes one shopper's cart requests serially.
 */
(function () {
  window.theme = window.theme || {};

  /** @type {Promise<any> | null} */
  var inFlight = null;

  function cartUrl() {
    return (window.theme.routes && window.theme.routes.cart_url) || '/cart';
  }

  /**
   * Reads the cart as JSON, deduplicating concurrent callers.
   *
   * `{ fresh: true }` guarantees the resolved cart was fetched AFTER this call
   * — required right after the caller's own mutation, because a request that
   * started before it would otherwise be joined and hand back pre-mutation
   * state (and, worse, pre-mutation line keys). A fresh call chains after any
   * in-flight request and REPLACES it as the shared promise, so plain callers
   * arriving afterwards join the post-mutation read.
   *
   * Rejects rather than resolving when the cart cannot be read. Shopify
   * rate-limits the cart endpoints and answers 429 with an HTML body, so an
   * unchecked .json() threw a SyntaxError indistinguishable from a real parse
   * failure. Callers must be able to tell "the cart is X" from "I could not
   * read the cart": the second is never safe to treat as data, and the cart
   * engine refuses to write line keys it could not verify.
   *
   * @param {{fresh?: boolean}} [options]
   * @returns {Promise<any>} The parsed cart (shared - treat as read-only).
   */
  function fetchCart(options) {
    var fresh = !!(options && options.fresh);
    if (inFlight && !fresh) return inFlight;

    var previous = inFlight;
    var chain = previous ? previous.catch(function () {}) : Promise.resolve();

    var promise = chain
      .then(function () {
        // cache: 'no-store' is load-bearing: /cart.js responses Vary on Accept,
        // and on back/forward navigations the browser served a stale count
        // from the HTTP disk cache.
        return fetch(cartUrl() + '.js', { cache: 'no-store' }).then(function (response) {
          if (!response.ok) {
            var error = new Error('Cart read failed: ' + response.status);
            error.status = response.status;
            throw error;
          }
          return response.json();
        });
      })
      .finally(function () {
        if (inFlight === promise) inFlight = null;
      });

    inFlight = promise;
    return promise;
  }

  /**
   * Total quantity of a variant across every rendered cart row.
   *
   * Counted from the DOM (the cart is server-rendered into every page) so the
   * add-to-cart cap costs no network, and summed ACROSS lines because one
   * variant can occupy several cart lines — an upsell-stamped line and a plain
   * line are two lines but one variant.
   *
   * @param {string|number} variantId
   * @returns {number}
   */
  function renderedVariantQuantity(variantId) {
    var id = String(variantId || '');
    if (!id) return 0;
    var rows = document.querySelectorAll('[data-line-item-key][data-variant-id="' + id + '"]');
    var total = 0;
    for (var i = 0; i < rows.length; i++) {
      var input = rows[i].querySelector('input[data-index]');
      var value = input ? parseInt(input.value, 10) : parseInt(rows[i].getAttribute('data-quantity'), 10);
      if (!isNaN(value)) total += value;
    }
    return total;
  }

  /**
   * Disables add buttons for variants that already sit at the per-variant cap
   * (settings.cart_max_per_variant), counting ACROSS cart lines. UI-only: the
   * authoritative check is the add-to-cart pre-check in ProductForm and in the
   * upsell card's own submit handler.
   *
   * Used by the cart upsell cards, whose buttons carry data-variant-id.
   *
   * Capping is applied unconditionally rather than only on the transition into
   * it: a card whose variant select moved from one capped variant to another
   * kept the marker from the first, and a transition-only check then matched
   * neither branch and left the button enabled over the cap.
   *
   * Un-capping only re-enables a button this function disabled, and never one
   * whose variant is unavailable — `data-available="false"` is the card's own
   * reason for the button being disabled and outranks the cap.
   *
   * @param {ParentNode} root - Container to scan for add buttons.
   * @param {{items: Array<{variant_id: number, quantity: number}>}} cart
   */
  function applyVariantCaps(root, cart) {
    var max = parseInt(window.theme.settings && window.theme.settings.cartMaxPerVariant, 10) || 0;
    if (!root || max <= 0) return;

    var perVariant = {};
    var items = (cart && cart.items) || [];
    for (var i = 0; i < items.length; i++) {
      var key = String(items[i].variant_id);
      perVariant[key] = (perVariant[key] || 0) + items[i].quantity;
    }

    var message = (window.theme.quickOrderListStrings && window.theme.quickOrderListStrings.maxError)
      ? window.theme.quickOrderListStrings.maxError.replace('[max]', String(max))
      : '';

    root.querySelectorAll('button[data-variant-id]').forEach(function (button) {
      var capped = (perVariant[String(button.dataset.variantId)] || 0) >= max;
      if (capped) {
        button.setAttribute('data-max-capped', '');
        button.disabled = true;
        if (message) button.title = message;
      }
      else if (button.hasAttribute('data-max-capped')) {
        button.removeAttribute('data-max-capped');
        button.removeAttribute('title');
        if (button.getAttribute('data-available') !== 'false') button.disabled = false;
      }
    });
  }

  window.theme.cartState = {
    fetch: fetchCart,
    renderedVariantQuantity: renderedVariantQuantity,
  };

  /**
   * Quantity of a variant in a cart payload, summed ACROSS lines: one variant
   * can occupy several lines (an upsell-stamped line beside a plain one) but it
   * is still one variant.
   *
   * @param {{items: Array<{variant_id: number, quantity: number}>}} cart
   * @param {string|number} variantId
   * @returns {number}
   */
  function variantQuantityInCart(cart, variantId) {
    var id = String(variantId || '');
    var items = (cart && cart.items) || [];
    var total = 0;
    for (var i = 0; i < items.length; i++) {
      if (String(items[i].variant_id) === id) total += items[i].quantity;
    }
    return total;
  }

  window.theme.cartLimits = {
    get max() {
      return parseInt(window.theme.settings && window.theme.settings.cartMaxPerVariant, 10) || 0;
    },
    applyVariantCaps: applyVariantCaps,
    variantQuantityInCart: variantQuantityInCart,
    message: function () {
      var max = parseInt(window.theme.settings && window.theme.settings.cartMaxPerVariant, 10) || 0;
      return (window.theme.quickOrderListStrings && window.theme.quickOrderListStrings.maxError)
        ? window.theme.quickOrderListStrings.maxError.replace('[max]', String(max))
        : '';
    },
  };
})();
